import { useState } from 'react';
import { sendFeedback } from '../../services/FeedbackService';
import { getErrorMessage } from '../../utils/errorMessage';

const TYPES = [
    { value: 'feedback', label: 'Feedback' },
    { value: 'bug', label: 'Bug report' },
    { value: 'doubt', label: 'Doubt' },
];

/**
 * Discreet "Contact" link in the footer that opens a modal to send feedback,
 * a bug report or a doubt. Renders its own trigger and modal so the footer
 * only needs to mount this component.
 */
function FeedbackModal() {
    const [show, setShow] = useState(false);
    const [sent, setSent] = useState(false);
    const [type, setType] = useState('feedback');
    const [message, setMessage] = useState('');
    const [sending, setSending] = useState(false);
    const [error, setError] = useState('');

    function open() {
        setError('');
        setSent(false);
        setShow(true);
    }

    function close() {
        if (sending) return;
        setShow(false);
    }

    function submit(event) {
        event.preventDefault();

        const trimmed = message.trim();
        if (!trimmed) {
            setError('Please write a message.');
            return;
        }

        setSending(true);
        setError('');
        sendFeedback({
            type,
            message: trimmed,
            page: typeof window !== 'undefined' ? window.location.href : '',
            userAgent:
                typeof navigator !== 'undefined' ? navigator.userAgent : '',
        })
            .then(() => {
                setSending(false);
                setSent(true);
                setMessage('');
                setType('feedback');
            })
            .catch(err => {
                setSending(false);
                setError(getErrorMessage(err));
            });
    }

    return (
        <>
            <li className="list-inline-item px-0 px-sm-2">
                <button
                    type="button"
                    className="btn btn-link p-0 align-baseline"
                    onClick={open}
                >
                    Contact
                </button>
            </li>
            {show && (
                <>
                    <div
                        className="modal fade show d-block"
                        tabIndex="-1"
                        role="dialog"
                        aria-modal="true"
                        aria-label="Send us a message"
                    >
                        <div className="modal-dialog modal-dialog-centered">
                            <div className="modal-content">
                                <div className="modal-header border-0 pb-0">
                                    <h3 className="modal-title h4">
                                        {sent
                                            ? 'Message sent'
                                            : 'Send us a message'}
                                    </h3>
                                    <button
                                        type="button"
                                        className="btn-close"
                                        aria-label="Close"
                                        onClick={close}
                                    ></button>
                                </div>
                                {sent ? (
                                    <>
                                        <div className="modal-body pt-2">
                                            <p className="text-gray-600 mb-0">
                                                Thank you! Your message was
                                                received and our team will get
                                                back to you if needed.
                                            </p>
                                        </div>
                                        <div className="modal-footer border-0 pt-0">
                                            <button
                                                className="btn btn-primary"
                                                onClick={close}
                                            >
                                                Close
                                            </button>
                                        </div>
                                    </>
                                ) : (
                                    <form onSubmit={submit}>
                                        <div className="modal-body pt-2">
                                            <div className="mb-3">
                                                <label
                                                    className="form-label"
                                                    htmlFor="feedbackType"
                                                >
                                                    Type
                                                </label>
                                                <select
                                                    id="feedbackType"
                                                    className="form-select"
                                                    value={type}
                                                    onChange={e =>
                                                        setType(e.target.value)
                                                    }
                                                    disabled={sending}
                                                >
                                                    {TYPES.map(option => (
                                                        <option
                                                            key={option.value}
                                                            value={option.value}
                                                        >
                                                            {option.label}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                            <div className="mb-3">
                                                <label
                                                    className="form-label"
                                                    htmlFor="feedbackMessage"
                                                >
                                                    Message
                                                </label>
                                                <textarea
                                                    id="feedbackMessage"
                                                    className="form-control"
                                                    rows="5"
                                                    maxLength={4000}
                                                    placeholder="Describe your feedback, the bug or your question..."
                                                    value={message}
                                                    onChange={e =>
                                                        setMessage(
                                                            e.target.value,
                                                        )
                                                    }
                                                    disabled={sending}
                                                    required
                                                ></textarea>
                                            </div>
                                            {error && (
                                                <div className="alert alert-danger py-2 mb-0">
                                                    {error}
                                                </div>
                                            )}
                                        </div>
                                        <div className="modal-footer border-0 pt-0">
                                            <button
                                                type="button"
                                                className="btn btn-outline-gray-600"
                                                onClick={close}
                                                disabled={sending}
                                            >
                                                Cancel
                                            </button>
                                            <button
                                                type="submit"
                                                className="btn btn-primary"
                                                disabled={sending}
                                            >
                                                {sending
                                                    ? 'Sending...'
                                                    : 'Send'}
                                            </button>
                                        </div>
                                    </form>
                                )}
                            </div>
                        </div>
                    </div>
                    <div className="modal-backdrop fade show"></div>
                </>
            )}
        </>
    );
}

export default FeedbackModal;
